import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import './online-access.scss'
import { onlineActivation, onlineActivationWithSN, getOnlineAccessActivationModels } from '../../utils/requests/onlineActivation'
import { privacyUrl } from '../../utils/fixedUrls'
import { publishAccountUpdate } from '../../utils/requests/mqttPublishAPI'
import Button from '../common/Button'
import { toast } from '../common/Toast'
import Dropdown from '../common/Dropdown'
import LoadingSpinner from '../common/LoadingSpinner'

const normalizeString = (str) => (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '')

const OnlineAccess = (props) => {
  const { t } = useTranslation()
  const [activationByToken] = useState(
    !!props?.querystring?.token || props.isBravoG4OnlineAccess || props.isJeniOnlineAccess || props?.querystring?.isTokenActivation,
  )
  const initialProduct = props?.querystring?.product || props?.model || ''
  const [formData, updateFormData] = useState(() => ({
    token_1: props?.querystring?.token ? (props?.querystring?.token === '000-000' ? '' : formatToken(props?.querystring?.token)) : '',
  }))
  const [fieldNumber, setFieldNumber] = useState([1])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingModels, setIsLoadingModels] = useState(!props.activationModels)
  const [selectedProduct, setSelectedProduct] = useState(() => {
    // Pre-select product immediately if models are already available
    if (props.activationModels && initialProduct) {
      const urlSlug = normalizeString(initialProduct)
      const match = props.activationModels.find((opt) => normalizeString(opt.value).includes(urlSlug))
      return match ? match.key : ''
    }
    return ''
  })
  const { toggleShowOnlineAccess, setIsSuccessOnlineActivation } = props
  const [activationModels, setActivationModels] = useState(props.activationModels || [])
  const isBravoSelected = normalizeString(selectedProduct).includes('bravo')
  const isJeniSelected = normalizeString(selectedProduct).includes('jeni')

  useEffect(() => {
    // Skip fetch if models were passed as props
    if (props.activationModels) return

    getOnlineAccessActivationModels()
      .then((models) => {
        const options = models.data.map((name) => ({
          key: name,
          value: name,
        }))
        setActivationModels(options)

        if (initialProduct && options.length) {
          const urlSlug = normalizeString(initialProduct)
          const match = options.find((opt) => normalizeString(opt.value).includes(urlSlug))
          if (match) {
            setSelectedProduct(match.key)
          }
        }
      })
      .catch(() => {
        setActivationModels([])
      })
      .finally(() => {
        setIsLoadingModels(false)
      })
  }, [])

  useEffect(() => {
    if (!initialProduct || !activationModels.length) return

    const urlSlug = normalizeString(initialProduct)
    const match = activationModels.find((opt) => normalizeString(opt.value).includes(urlSlug))

    if (match) {
      setSelectedProduct(match.key)
    } else {
      setSelectedProduct('')
    }
  }, [props?.querystring?.product, activationModels])

  const handleChange = (e) => {
    const maxLength = isJeniSelected ? 9 : 7
    if (e.target.value.length > maxLength) {
      return
    }

    updateFormData({
      ...formData,
      [e.target.name]: formatToken(e.target.value),
    })
  }

  function formatToken(token) {
    const tokenWithoutHyphens = token.split('-').join('')
    if (!tokenWithoutHyphens) return ''

    const groupSize = tokenWithoutHyphens.length > 6 ? 4 : 3
    const regex = new RegExp(`.{1,${groupSize}}`, 'g')
    const groups = tokenWithoutHyphens.toUpperCase().match(regex)
    return groups ? groups.join('-') : tokenWithoutHyphens.toUpperCase()
  }

  const addField = () => {
    if (fieldNumber.length >= 3) {
      return toast.error(t('online-access.limit'))
    }
    const incrementer = fieldNumber.length + 1
    setFieldNumber([...fieldNumber, incrementer])
  }

  const removeField = (index) => {
    if (fieldNumber.length <= 1) {
      return
    }
    fieldNumber.splice(index, 1)
    setFieldNumber([...fieldNumber])
  }

  const findAssociatedProducts = () => {
    let arr = []
    for (const [key, value] of Object.entries(props.userProducts.products)) {
      if (value.association_active) {
        arr.push({ serial_number: value.serial_number })
      }
    }
    return arr
  }

  const submitActivation = () => {
    if (!props.authedUser.cognitoSub) {
      toast.error(t('error.failure_msg'))
      return
    }

    setIsLoading(true)

    const model_id = props.querystring?.model_id ?? ''

    if (activationByToken) {
      if (!selectedProduct) {
        setIsLoading(false)
        toast.error(t('online-access.model_required'))
        return
      }

      for (let i = 0; i < fieldNumber.length; i++) {
        const tokenValue = (formData[`token_${i + 1}`] || '').trim()

        if (!tokenValue) {
          setIsLoading(false)
          toast.error(t('online-access.TokenNotProvided'))
          return
        }

        if (isBravoSelected) {
          const bravoPattern = /^[A-Z0-9]{3}-[A-Z0-9]{3}$/
          if (!bravoPattern.test(tokenValue)) {
            setIsLoading(false)
            toast.error(t('online-access.TokenInvalid'))
            return
          }
        }

        if (isJeniSelected) {
          const jeniPattern = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/
          if (!jeniPattern.test(tokenValue)) {
            setIsLoading(false)
            toast.error(t('online-access.TokenInvalid'))
            return
          }
        }

        const body = {
          token: formData[`token_${i + 1}`],
          email: props.authedUser.email,
          model: selectedProduct,
        }

        const activationRequest = props.serialNumber ? onlineActivationWithSN(props.serialNumber, body) : onlineActivation(body)
        activationRequest
          .then((data) => {
            if (Array.isArray(data)) {
              if (data[0].status === 1) {
                toast.success(t('online-access.access_activated'))
                setIsSuccessOnlineActivation(true)
              } else {
                const errorCode = data[0]?.error?.code
                toast.error(t(`online-access.${errorCode}`))
              }
            } else {
              if (data.status === 1) {
                toast.success(t('online-access.access_activated'))
                setIsSuccessOnlineActivation(true)
              } else {
                const errorCode = data?.error?.code
                toast.error(t(`online-access.${errorCode}`))
              }
            }
          })
          .catch((error) => {
            const errorCode = error.response?.data?.code || 'failure_msg'
            toast.error(t(`error.${errorCode}`))
          })
          .finally(() => {
            setIsLoading(false)
            toggleShowOnlineAccess()
          })
      }
    } else {
      const body = {
        serial_number: props.querystring?.sn,
        email: props.authedUser.email,
        model_id: model_id,
      }
      onlineActivation(body)
        .then((data) => {
          if (data[0].status === 1) {
            toast.success(t('online-access.access_activated'))
            setIsSuccessOnlineActivation(true)
            if (props.querystring && props.querystring.publish) {
              const unixTimestampSeconds = Math.round(new Date().getTime() / 1000)
              const body = [
                {
                  account_state: 'associated',
                  account_state_time_modified_utc_seconds: unixTimestampSeconds,
                  account_email: props.authedUser.email ? props.authedUser.email : '',
                  account_company_name: props.authedUser.company ? props.authedUser.company : '',
                  account_contact_name: `${props.authedUser.firstname ? props.authedUser.firstname : null} ${
                    props.authedUser.lastname ? props.authedUser.lastname : null
                  }`,
                  account_phone_number: props.authedUser.phone ? props.authedUser.phone : '',
                  account_address: props.authedUser.address1 ? props.authedUser.address1 : '',
                  account_city: props.authedUser.city ? props.authedUser.city : '',
                  account_subregion: props.authedUser.stateprovince ? props.authedUser.stateprovince : '',
                  account_country: props.authedUser.country ? props.authedUser.country : '',
                  account_zip_code: props.authedUser.postalcode ? props.authedUser.postalcode : '',
                  units: findAssociatedProducts(props.userProducts.products),
                },
              ]

              publishAccountUpdate(props.querystring?.sn, body).catch(() => toast.error(t('error.failure_msg')))
            }
          } else {
            toast.error(t(`online-access.${data[0]['error']['code']}`))
          }
        })
        .catch(() => toast.error(t('error.failure_msg')))
        .finally(() => {
          setIsLoading(false)
          toggleShowOnlineAccess()
        })
    }
  }

  const handleFields = (index) => {
    if (index === 0) {
      addField()
      return
    }

    removeField(index)
  }

  if (isLoadingModels) {
    return <LoadingSpinner />
  }

  return (
    <div className="online-activation-wrapper d-flex flex-align-center flex-column flex-justify-start">
      <h3 className="title">{t('online-access.online-activation')}</h3>
      {activationByToken ? (
        <div className="modal-content d-flex flex-justify-center flex-align-center flex-column">
          <div class="sub-title token">{t('online-access.token-subtitle')}</div>
          <div className="label token-label">{t('model')}</div>
          <div className="input d-flex flex-justify-center">
            <Dropdown
              options={activationModels}
              className="online-access-product-dropdown"
              initialSelectedKey={selectedProduct}
              onChange={(option) => setSelectedProduct(option.key)}
              placeholder={t('select_product')}
              isDisabled={props.model}
            />
          </div>
          {/* <div className="sub-title token">{t('online-access.token-subtitle')}</div> */}
          <div className="label token-label">{t('online-access.activation-code')}</div>
          {fieldNumber.length &&
            fieldNumber.map((field, index) => (
              <div className="input d-flex flex-justify-center" key={index}>
                <input
                  type="text"
                  name={`token_${index + 1}`}
                  placeholder={
                    isBravoSelected
                      ? t('online-access.enter-code-bravo')
                      : isJeniSelected
                        ? t('online-access.enter-code-jeni')
                        : t('online-access.enter-code-bravo')
                  }
                  onChange={handleChange}
                  value={(formData && formData[`token_${index + 1}`]) || ''}
                  className={props.isBravoG4OnlineAccess || props.isJeniOnlineAccess ? 'token-input single-activation' : 'token-input'}
                />
                {!props.isBravoG4OnlineAccess && !props.isJeniOnlineAccess && (
                  <div className={index === 0 ? 'plus' : 'minus'} onClick={() => handleFields(index)}>
                    {`${index === 0 ? '+' : '-'}`}
                  </div>
                )}
              </div>
            ))}
        </div>
      ) : (
        <div className="modal-content d-flex flex-justify-center flex-align-center flex-column">
          <div className="sub-title">{t('online-access.sn-subtitle')}</div>
          <div className="label">{t('serial_number')}</div>
          <div className="serial-number">{props.querystring?.sn}</div>
        </div>
      )}
      <div className="onlineaccess-btn">
        <Button
          className={`submit btn ${activationByToken && !selectedProduct ? 'disabled' : ''}`}
          isLoading={isLoading}
          disabled={activationByToken && !selectedProduct}
          onClick={() => submitActivation()}
        >
          {t('submit')}
        </Button>
        {activationByToken && (
          <p className="privacy-statement">
            {t('online-access.button_accept_coltene_privacy')}{' '}
            <span>
              <a href={privacyUrl} target="_blank" rel="noreferrer" className="external-link">
                {t('privacy_policy')}
              </a>
            </span>
          </p>
        )}
        {activationByToken && isJeniSelected && <p className="activation-code-message">{t('online-access.navigate_service_support_message')}</p>}
      </div>
    </div>
  )
}

function mapStateToProps({ onlineAccess, userProducts, authedUser }) {
  return {
    querystring: onlineAccess,
    userProducts: userProducts,
    authedUser: authedUser,
  }
}

export default connect(mapStateToProps)(OnlineAccess)

